import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';

import Searcher from 'components/DesignSystem/Searcher';
import MobileFilterSheet from 'components/Layout/MobileShell/MobileFilterSheet';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import { ListedProduct } from 'helper/products';
import { UserModalFilters } from 'helper/userFilters';
import { ListedCompany } from 'helper/companies';
import {
  onTextChange,
  onExclusiveCheckChange,
  onCheckChange,
} from 'helper/controlledField';
import {
  loadUserFilters,
  onUserFiltersClear,
  onUserFiltersSave,
  onProductCheckChange,
} from 'helper/filterModals';

import './index.scss';

type FilterCategoryKey = 'produto' | 'empresa' | 'status';

interface FilterSheetProps {
  open: boolean,
  closeModal: () => void,
  filters: UserModalFilters,
  setFilters: React.Dispatch<React.SetStateAction<UserModalFilters>>,
  productsLoading: boolean,
  products: ListedProduct[] | undefined,
  companiesLoading: boolean,
  companies: ListedCompany[] | undefined,
}

export default function FilterSheet({
  open,
  closeModal,
  filters,
  setFilters,
  productsLoading,
  products,
  companiesLoading,
  companies,
}: FilterSheetProps) {
  const [selectedProducts, setSelectedProducts] = useState([] as number[]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isExpiringIn30, setisExpiringIn30] = useState(false);
  const [isExpiringIn60, setisExpiringIn60] = useState(false);
  const [openCategory, setOpenCategory] = useState<FilterCategoryKey | ''>('');
  const companyNames = companies ? companies.map(
    (company) => company.nome_fantasia,
  ) : [];
  const filteredCompanyNames = companyNames.filter(
    (name) => name.toLowerCase().includes(companyQuery.toLowerCase()),
  );
  const statusCount = [isEnabled, isDisabled, isExpiringIn30, isExpiringIn60]
    .filter(Boolean).length;

  useEffect(() => {
    if (open) {
      loadUserFilters(
        filters,
        setSelectedProducts,
        setSelectedCompany,
        setIsEnabled,
        setIsDisabled,
        setisExpiringIn30,
        setisExpiringIn60,
      );
      setCompanyQuery('');
      setOpenCategory('');
    }
  }, [open, filters]);

  const toggleCategory = (category: FilterCategoryKey) => () => {
    setOpenCategory((current) => (current === category ? '' : category));
  };

  const clearDraft = onUserFiltersClear(
    setSelectedProducts,
    setSelectedCompany,
    setIsEnabled,
    setIsDisabled,
    setisExpiringIn30,
    setisExpiringIn60,
  );

  const saveDraft = onUserFiltersSave(
    {
      selectedProducts,
      selectedCompany,
      isEnabled,
      isDisabled,
      isExpiringIn30,
      isExpiringIn60,
    },
    setFilters,
    closeModal,
  );

  return (
    <MobileFilterSheet
      open={open}
      dismiss={closeModal}
      title="Filtrar usuários"
      className="users-filter-sheet"
      onClear={clearDraft}
      onApply={saveDraft}
    >
      <Section
        label="Produto"
        valueText={selectedProducts.length ? String(selectedProducts.length) : undefined}
        open={openCategory === 'produto'}
        onToggle={toggleCategory('produto')}
      >
        {productsLoading && <Spinner animation="border" size="sm" />}
        {!productsLoading && products && products.map((product) => (
          <OptionRow
            key={product.id}
            label={product.nome}
            selectionType="multiple"
            selected={selectedProducts.includes(product.id)}
            onSelect={onProductCheckChange(
              product.id,
              selectedProducts,
              setSelectedProducts,
            )}
          />
        ))}
      </Section>

      <Section
        label="Empresa"
        valueText={selectedCompany || undefined}
        open={openCategory === 'empresa'}
        onToggle={toggleCategory('empresa')}
      >
        {companiesLoading && <Spinner animation="border" size="sm" />}
        {!companiesLoading && (
          <>
            <Searcher
              className="users-filter-sheet-company-search"
              value={companyQuery}
              onChange={onTextChange(setCompanyQuery)}
              placeholder="Buscar empresa"
            />
            <div className="users-filter-sheet-company-list">
              {filteredCompanyNames.map((name) => (
                <OptionRow
                  key={name}
                  label={name}
                  selectionType="single"
                  selected={selectedCompany === name}
                  onSelect={() => setSelectedCompany(
                    selectedCompany === name ? '' : name,
                  )}
                />
              ))}
            </div>
          </>
        )}
      </Section>

      <Section
        label="Status"
        valueText={statusCount ? String(statusCount) : undefined}
        open={openCategory === 'status'}
        onToggle={toggleCategory('status')}
      >
        <OptionRow
          label="Habilitado"
          selectionType="single"
          selected={isEnabled}
          onSelect={onExclusiveCheckChange(
            isEnabled,
            isDisabled,
            setIsEnabled,
            setIsDisabled,
          )}
        />
        <OptionRow
          label="Desabilitado"
          selectionType="single"
          selected={isDisabled}
          onSelect={onExclusiveCheckChange(
            isDisabled,
            isEnabled,
            setIsDisabled,
            setIsEnabled,
          )}
        />
        <OptionRow
          label="Vence em até 30 dias"
          selectionType="multiple"
          selected={isExpiringIn30}
          onSelect={onCheckChange(isExpiringIn30, setisExpiringIn30)}
        />
        <OptionRow
          label="Vence entre 30 e 60 dias"
          selectionType="multiple"
          selected={isExpiringIn60}
          onSelect={onCheckChange(isExpiringIn60, setisExpiringIn60)}
        />
      </Section>
    </MobileFilterSheet>
  );
}
